import React, {
  useState,
  InputHTMLAttributes,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';
import { Icon } from '..';
import { FieldValues, UseFormRegister } from 'react-hook-form';
import { Transition } from '@headlessui/react';
import { filterSearchOptions } from './Search';

export interface IsolatedSearchProps
  extends InputHTMLAttributes<HTMLInputElement> {
  register?: UseFormRegister<FieldValues>;
  searchOptions?: string[];
  positioning?: 'relative' | 'absolute';
  setFocus: Dispatch<SetStateAction<boolean>>;
  focus: boolean;
  style?: any;
  errors?: any;
  isValid?: boolean;
  readOnly?: boolean;
  maxListHeight?: string;
}

export const IsolatedSearch = ({
  type,
  name,
  placeholder,
  value,
  className,
  register,
  onChange,
  positioning,
  searchOptions,
  setFocus,
  focus,
  style,
  errors,
  isValid,
  onBlur,
  readOnly,
  maxListHeight,
  ...rest
}: IsolatedSearchProps) => {
  const [search, setSearch] = useState<string>(value?.toString() || '');
  const [visible, setVisible] = useState<boolean>(false);
  const [highlighted, setHighlighted] = useState<number>(-1);
  const [visibleOptions, setVisibleOptions] = useState<string[]>(
    searchOptions || []
  );
  const searchOptionsRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const highlightedItem =
      searchOptionsRef?.current?.querySelector('.highlighted');
    if (highlightedItem) {
      highlightedItem?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [highlighted]);

  useEffect(() => {
    if (value?.toString() !== search) {
      setSearch(value?.toString() || '');
    }
  }, [value]);

  useEffect(() => {
    filterSearchOptions(search, searchOptions, setVisibleOptions);
  }, [searchOptions?.length, searchOptions]);

  useEffect(() => {
    if (value?.toString() !== search) {
      setSearch(value?.toString() || '');
    }
  }, [value]);

  useEffect(() => {
    filterSearchOptions(search, searchOptions, setVisibleOptions);
  }, [searchOptions?.length, searchOptions]);

  const handleSearchChange = (e: any, option: string) => {
    const { value } = e.target;
    e.stopPropagation();
    onChange && onChange(e);
    setSearch(value);

    // If an option was passed, that means there was an onClick event
    if (option) {
      setSearch(option);
      setVisible(false);
    }
    // If the change was not an onClick event
    else {
      if (value.length && value !== search) {
        setVisible(true);
      } else setVisible(false);

      setSearch(value);

      filterSearchOptions(value, searchOptions, setVisibleOptions);
    }
  };

  const handleOnclick = (
    option: string,
    e: React.SyntheticEvent<EventTarget>
  ): void => {
    e.preventDefault();
    if (search !== option) {
      handleSearchChange(e, option);
      // setSearch(option);
      // setVisible(false);
    }
  };

  const handleKeypress = (e: any) => {
    if (e.code === 'Enter') {
      if (highlighted !== -1) {
        const highlightedOption = visibleOptions
          ? visibleOptions[highlighted]
          : '';

        onChange && onChange(e);
        setSearch(highlightedOption);
        setVisible(false);
        setHighlighted(-1);
      }
    }
    if (visible && e.code === 'ArrowDown') {
      if (visibleOptions?.length && highlighted < visibleOptions.length - 1)
        setHighlighted(highlighted + 1);
    }
    if (visibleOptions?.length && visible && e.code === 'ArrowUp') {
      if (highlighted > -1) setHighlighted(highlighted - 1);
    }
  };

  useEffect(() => {
    if (value) setSearch(value?.toString());
  }, [value]);

  return (
    <>
      <div
        style={style}
        onClick={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        className={`relative flex justify-between items-center px-0 py-2 border ${
          !focus
            ? ' border-thryv-gray-light-500 placeholder-thryv-steel'
            : 'border-thryv-black-500 ring-1 ring-thryv-black-500'
        } rounded transition-all bg-transparent  ${className || ''} ${
          (errors && name && errors[name] && errors[name].message) || !isValid
            ? 'border-utility-error-danger peer-focus:border-utility-error-danger'
            : ''
        } ${
          focus &&
          ((errors && name && errors[name] && errors[name].message) || !isValid)
            ? 'border-utility-error-danger peer-focus:border-utility-error-danger ring-1 ring-utility-error-danger'
            : ''
        }`}
      >
        <input
          type={type}
          name={name}
          placeholder={placeholder}
          className={`peer appearance-none 
             ${
               readOnly ? 'text-thryv-gray-medium-500' : 'text-thryv-black-500'
             } text-form-text border-none w-full ring-0 focus:outline-none pl-4 py-0 bg-transparent`}
          {...(register && {
            ...register(`${name}`),
          })}
          onChange={(e) => {
            handleSearchChange(e, '');
            onChange && onChange(e);
          }}
          value={search}
          onKeyUp={handleKeypress}
          onBlur={onBlur && onBlur}
          autoComplete="off"
          readOnly={readOnly || false}
          {...rest}
        />
        <span className="mr-[14px]">
          <Icon
            type="solid"
            variant={readOnly ? 'lock' : 'search'}
            color={
              (errors && name && errors[name] && errors[name].message) ||
              !isValid
                ? '#DF2A2A'
                : (focus && '#231F20') || '#A3A5A7'
            }
            height="17px"
            role="img"
          >
            <title>Magnifying Glass Icon</title>
          </Icon>
        </span>
      </div>
      <Transition
        show={visible}
        as="div"
        enter="transition ease-in duration-300 relative z-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-out duration-200"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        {visibleOptions.length ? (
          <div
            className={`overflow-auto w-full mt-3 origin-top-left bg-white rounded shadow-thryv focus:outline-none ${
              positioning || 'absolute'
            } z-100`}
            style={{ maxHeight: maxListHeight }}
            ref={searchOptionsRef}
          >
            <div className="">
              {visibleOptions.map((option, i) => (
                <div key={i}>
                  <button
                    className={` flex justify-start hover:bg-thryv-white-300 ${
                      highlighted === i && 'bg-thryv-white-300 highlighted'
                    } duration-300 group items-center w-full px-2 py-2 text-[12px] font-open-sans capitalize transition-all`}
                    onClick={(e) => handleOnclick(option, e)}
                  >
                    <span className="ml-1">{option}</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          ''
        )}
      </Transition>
    </>
  );
};
